import React, { useState } from "react";
import {
  useNavigate,
  Link,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { adminRoutes } from "../../../routers/AdminRouters";
import {
  useGetSessionFormatQuery,
  useGetSessionFormatBloacksBySessionFormatIdQuery,
  useGetSessionSummaryBySessionIdQuery,
} from "../../../services";
import { SessionSummary } from "./exercisebuildercomponents";

const SessionSummaryBuilder: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category");
  const { sessionid } = useParams<{ sessionid?: string }>();
  const [isSessionSummaryExist, setIsSessionSummaryExist] = useState(false)

  const [isSessionSummarySelected, setIsSessionSummarySelected] =
    useState(true); // Changed from false to true
  const [selectedSubFormatIdx, setSelectedSubFormatIdx] = useState<
    number | null
  >(null);

  // Track optional difficulty filter
  const [selectedDifficulty, setSelectedDifficulty] = useState<
    "BEGINNER" | "ADVANCED" | null
  >(null);
  const { data: summaryQueryResult, isLoading: isSummaryQueryLoading } = useGetSessionSummaryBySessionIdQuery(
    { sessionid: sessionid ?? "", limit: 100, page: 1 },
    { skip: !isSessionSummarySelected || !sessionid, refetchOnMountOrArgChange: true }
  );

  const { data: sessionFormat } = useGetSessionFormatQuery(
    sessionid ?? "",
    { refetchOnMountOrArgChange: true }
  );


  const formatList = sessionFormat?.results || [];

  const [selectedFormatIdx, setSelectedFormatIdx] = useState(0);
  const selectedFormat = formatList[selectedFormatIdx];
  const selectedFormatId = selectedFormat?._id;
  const selectedFormatName = selectedFormat?.name || "Format";
  const selectedFormatDuration = selectedFormat?.duration || 0; // <-- Add this line

  const {
    data: sessionSummaryData,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useGetSessionFormatBloacksBySessionFormatIdQuery(
    {
      sessionFormatId: selectedFormatId || "",
      difficulty: undefined,
    },
    {
      skip: !selectedFormatId,
      refetchOnMountOrArgChange: true,
    },
  );
  console.log(sessionSummaryData, "sessionSummaryData");




  const { data: sessionFormatBlocks, refetch: refetchFormatBlocks } =
    useGetSessionFormatBloacksBySessionFormatIdQuery(
      selectedFormatId
        ? {
          sessionFormatId: selectedFormatId,
          difficulty: selectedDifficulty ?? undefined,
        }
        : { sessionFormatId: "", difficulty: undefined },
      { skip: !selectedFormatId },
    );

  React.useEffect(() => {
    if (selectedFormatId) {
      refetchFormatBlocks();
    }
  }, [selectedFormatId, selectedDifficulty, refetchFormatBlocks]);

  const themeBox = theme === "dark";
  const borderColor = themeBox ? "#232323" : "#E5E7EB";
  const bgCard = themeBox
    ? "bg-[#121212] border-[#232323]"
    : "bg-white border-gray-200";

  const [formatBlocks, setFormatBlocks] = useState<
    {
      id: string;
      name: string;
      fields: string[];
      fieldValues?: Record<string, number>;
      workPeriod?: string;
      restPeriod?: string;
      blockTiming?: string;
      restTime?: string;
    }[]
  >([]);

  const onSaveMatrix = async () => {
    try {
      navigate(
        `${adminRoutes.sessionPreview.replace(
          ":sessionid",
          String(sessionid || ""),
        )}${categoryId ? `?category=${encodeURIComponent(categoryId)}` : ""}`,
      );
    } catch (e) {
      console.error("Matrix submission failed:", e);
    }
  };


  const handleSessionSummaryClick = () => {
    setIsSessionSummarySelected(true);
    setSelectedDifficulty(null);
    setSelectedSubFormatIdx(null);
  };

  const handleSubFormatClick = (idx: number) => {
    setSelectedSubFormatIdx(idx);
    setSelectedFormatIdx(idx);
  };

  return (
    <div
      className={`pt-20 pb-8 min-h-screen ${themeBox ? "bg-[#0B0B0B]" : "bg-gray-50"
        } px-4 md:px-6 lg:px-8`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Header */}
      <div className="mb-4">
        <h1
          className={`text-xl font-semibold ${themeBox ? "text-gray-100" : "text-gray-900"
            }`}
        >
          Session Overview
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
            to={`${adminRoutes.sessionList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Sessions
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            Create New Session
          </span>
        </div>
      </div>

      {/* Card */}
      <div
        className={`rounded-lg border ${bgCard} relative overflow-hidden`}
        style={{ borderColor }}
      >

        <div className="flex">
          {/* Sidebar: Session Summary */}
          <div
            className={`flex flex-col w-64 border-r ${themeBox ? "border-[#232323]" : "border-gray-200"
              } py-4`}
          >
            <h4
              className={`px-4 pb-6 text-sm font-semibold ${themeBox ? "text-gray-100" : "text-gray-800"
                }`}
            >
              Session Summary
            </h4>
            <div className="px-5 relative">
              <div className="flex flex-col">
                {formatList.map((fmt: any, idx: number) => {
                  const isActive =
                    selectedFormatIdx === idx && !isSessionSummarySelected;

                  return (
                    <div key={fmt._id} className="relative z-10 flex items-stretch gap-4">
                      {/* Timeline Dot & Line */}
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div
                          className={`mt-2.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${isActive
                            ? "border-[#B6CB24] bg-[#B6CB24] shadow-[0_0_8px_rgba(182,203,36,0.6)]"
                            : themeBox
                              ? "border-[#444] bg-[#121212]"
                              : "border-gray-300 bg-white"
                            }`}
                        >
                          {isActive && (
                            <div className="w-1.5 h-1.5 rounded-full bg-black/20" />
                          )}
                        </div>
                        {/* Line to next node */}
                        <div className="flex-1 w-0 border-l border-dashed border-[#444] min-h-[40px]" />
                      </div>

                      {/* Format Content */}
                      <div className="flex-1 min-w-0 pb-6">
                        <button
                          onClick={() =>
                            navigate(
                              `${adminRoutes.sessionMetricsBuilder.replace(
                                ":sessionid",
                                String(sessionid || ""),
                              )}${categoryId
                                ? `?category=${encodeURIComponent(categoryId)}`
                                : ""
                              }`,
                            )
                          }
                          className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${isActive
                            ? themeBox
                              ? "bg-[#B6CB24]/10 border-[#B6CB24] text-[#F3F3F6]"
                              : "bg-lime-50 border-lime-500 text-lime-900"
                            : themeBox
                              ? "bg-transparent border-[#232323] text-gray-400 hover:border-[#444]"
                              : "bg-transparent border-gray-200 text-gray-600 hover:border-gray-300"
                            }`}
                        >
                          <span className="text-sm font-semibold truncate block">
                            {fmt.name}
                          </span>
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div className="relative z-10 flex flex-col">
                  <div className="flex items-start gap-4">
                    {/* Timeline Dot (No line below this one) */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div
                        className={`mt-2.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${isSessionSummarySelected
                          ? "border-[#B6CB24] bg-[#B6CB24] shadow-[0_0_8px_rgba(182,203,36,0.6)]"
                          : themeBox
                            ? "border-[#444] bg-[#121212]"
                            : "border-gray-300 bg-white"
                          }`}
                      >
                        {isSessionSummarySelected && (
                          <div className="w-1.5 h-1.5 rounded-full bg-black/20" />
                        )}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <button
                        onClick={handleSessionSummaryClick}
                        className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${isSessionSummarySelected
                          ? themeBox
                            ? "bg-[#B6CB24]/10 border-[#B6CB24] text-[#F3F3F6]"
                            : "bg-lime-50 border-lime-500 text-lime-900"
                          : themeBox
                            ? "bg-transparent border-[#232323] text-gray-400 hover:border-[#444]"
                            : "bg-transparent border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                      >
                        <span className="text-sm font-semibold truncate block">
                          Session Summary
                        </span>
                      </button>

                      {/* Sub-formats vertically stacked under Summary when active */}
                      {isSessionSummarySelected && (
                        <div className="mt-4 flex flex-col items-start px-4 space-y-3">
                          {formatList.map((fmt: any, idx: number) => (
                            <button
                              key={`summary-${fmt._id}`}
                              onClick={() => handleSubFormatClick(idx)}
                              className={`text-sm transition-colors ${selectedSubFormatIdx === idx
                                ? "text-[#B6CB24] font-bold"
                                : "text-gray-400 hover:text-gray-300"
                                }`}
                            >
                              {fmt.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            {isSessionSummarySelected ? (
              isSummaryLoading ? (
                <div className="p-6 text-center">
                  <div
                    className={`text-sm ${themeBox ? "text-gray-300" : "text-gray-600"
                      }`}
                  >
                    {isSummaryLoading || isSummaryQueryLoading ? "Loading session summary..." : "Loading session summary..."}
                  </div>
                </div>
              ) : summaryError ? (
                <div className="p-6 text-center">
                  <div className={`text-sm text-red-500`}>
                    Error loading session summary:{" "}
                    {JSON.stringify(summaryError)}
                  </div>
                </div>
              ) : (
                <SessionSummary
                  themeBox={themeBox}
                  selectedFormatId={selectedFormatId || ""}
                  sessionSummaryData={selectedSubFormatIdx !== null ? (sessionSummaryData?.results || []) : (summaryQueryResult?.results || [])}
                  formatName={selectedFormatName}
                  duration={selectedFormatDuration}
                  formatList={formatList}
                  isSessionSummary={selectedSubFormatIdx === null}
                />
              )
            ) : (
              <div className="p-6">
                <h3
                  className={`text-lg font-semibold mb-4 ${themeBox ? "text-gray-200" : "text-gray-800"
                    }`}
                >
                  Select Session Summary
                </h3>
                <div
                  className={`text-sm ${themeBox ? "text-gray-300" : "text-gray-600"
                    }`}
                >
                  Click on Session Summary to view the session details.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer: Actions */}
        <div
          className={`flex items-center justify-center px-6 py-4 border-t ${themeBox ? "border-[#232323]" : "border-gray-200"
            }`}
        >
          <div className="flex items-center gap-4">
            <button
              onClick={onSaveMatrix}
              className={`flex items-center gap-2 px-5 py-1.5 rounded-[10px] border border-[#C6D92C] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed ${theme === "dark"
                ? "bg-[#232323] text-gray-200 hover:bg-[#333333]"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.80) 0%, rgba(83,91,16,0.80) 100%)",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M15.5 15L17.5 17M16.5 11.5C16.5 9.01472 14.4853 7 12 7C9.51472 7 7.5 9.01472 7.5 11.5C7.5 13.9853 9.51472 16 12 16C14.4853 16 16.5 13.9853 16.5 11.5Z"
                  stroke="#C6D92C"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M15 21.5C16.8623 21.5 17.7935 21.5 18.5391 21.2286C19.789 20.7737 20.7737 19.789 21.2286 18.5391C21.5 17.7935 21.5 16.8623 21.5 15M9 21.5C7.13769 21.5 6.20653 21.5 5.46091 21.2286C4.21096 20.7737 3.22633 19.789 2.77138 18.5391C2.5 17.7935 2.5 16.8623 2.5 15M9 2.5C7.13769 2.5 6.20653 2.5 5.46091 2.77138C4.21096 3.22632 3.22633 4.21096 2.77138 5.46091C2.5 6.20653 2.5 7.13768 2.5 9M15 2.5C16.8623 2.5 17.7935 2.5 18.5391 2.77138C19.789 3.22632 20.7737 4.21096 21.2286 5.46091C21.5 6.20653 21.5 7.13768 21.5 9"
                  stroke="#F3F3F6"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {isSessionSummaryExist ? "Update & Preview" : "Save & Preview"}
            </button>
            <button
              className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium border ${themeBox
                ? "border-[#444] text-gray-300 hover:bg-[#1E1E1E]"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              onClick={() =>
                navigate(
                  `${adminRoutes.sessionMetricsBuilder.replace(
                    ":sessionid",
                    String(sessionid || ""),
                  )}${categoryId
                    ? `?category=${encodeURIComponent(categoryId)}`
                    : ""
                  }`,
                )
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M11 6H15.5C17.9853 6 20 8.01472 20 10.5C20 12.9853 17.9853 15 15.5 15H4"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.99998 12C6.99998 12 4.00001 14.2095 4 15C3.99999 15.7906 7 18 7 18"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Previous
            </button>

            <button
              onClick={() => navigate(adminRoutes.sessionList)}
              className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium border ${themeBox
                ? "border-[#444] text-gray-300 hover:bg-[#1E1E1E]"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.52165 21.5 5.28248 20.1088 3.89124C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.89124C2.5 5.28249 2.5 7.52166 2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28248 21.5 7.52165 21.5 12 21.5Z"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 9L9 14.9996M15 15L9 9.00039"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionSummaryBuilder;
